import { useState, useEffect } from "react";
import QuoteCarousel from "@/components/QuoteCarousel";
import { Loader2, CheckCircle, AlertTriangle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogoMark } from "@/components/Logo";
import { useToast } from "@/hooks/use-toast";

interface InviteInfo {
  creditorName: string;
  creditor: string;
  debtId: number;
  balance: number;
  verificationStatus: "pending" | "verified" | "failed";
  alreadySubmitted: boolean;
}

export default function CreditorPortalVerify() {
  const { toast } = useToast();
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [amount1, setAmount1] = useState("");
  const [amount2, setAmount2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [verified, setVerified] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError("No invite token found in this link.");
      setLoading(false);
      return;
    }

    fetch(`/api/creditor-portal/invite?token=${encodeURIComponent(token)}`, {
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json() as { error: string };
          throw new Error(data.error ?? "Invalid link");
        }
        return res.json() as Promise<InviteInfo>;
      })
      .then((data) => {
        setInviteInfo(data);
        if (data.verificationStatus === "verified") setVerified(true);
        if (data.verificationStatus === "failed") setFailed(true);
        if (!data.alreadySubmitted) {
          setLoadError("No banking details on file. Please submit your banking details first.");
        }
      })
      .catch((err: Error) => {
        setLoadError(err.message);
      })
      .finally(() => setLoading(false));
  }, [token]);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    const cents1 = Math.round(parseFloat(amount1) * 100);
    const cents2 = Math.round(parseFloat(amount2) * 100);

    if (isNaN(cents1) || isNaN(cents2) || cents1 <= 0 || cents2 <= 0) {
      toast({ title: "Invalid amounts", description: "Please enter the two deposit amounts from your bank statement.", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/creditor-portal/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, amount1: cents1, amount2: cents2 }),
      });

      const data = await res.json() as { success?: boolean; message?: string; error?: string };

      if (!res.ok) {
        if (res.status === 400) {
          setFailed(true);
        }
        toast({ title: "Verification failed", description: data.error ?? "Please try again.", variant: "destructive" });
        return;
      }

      setVerified(true);
      toast({ title: "Bank account verified!", description: "Your banking details are now confirmed. Payment will be scheduled soon." });
    } catch {
      toast({ title: "Network error", description: "Could not complete verification. Please check your connection.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-background">
      <div className="max-w-lg mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <LogoMark size={32} />
          <div>
            <span className="font-bold text-lg">DebtBridge Foundation</span>
            <p className="text-xs text-muted-foreground">Creditor Payment Portal — Verification</p>
          </div>
        </div>

        {loadError ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Cannot complete verification</p>
                  <p className="text-sm text-red-700 mt-1">{loadError}</p>
                  {!inviteInfo?.alreadySubmitted && (
                    <a
                      href={`/creditor-portal?token=${encodeURIComponent(token)}`}
                      className="inline-block mt-3 text-sm text-blue-700 underline"
                    >
                      Submit banking details first →
                    </a>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : verified ? (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-3">
                <ShieldCheck className="h-6 w-6 text-green-600" />
                <p className="font-semibold text-green-800 text-lg">Bank account verified!</p>
              </div>
              <p className="text-green-700 text-sm">Your ACH banking details have been successfully verified. DebtBridge will schedule your payment shortly and you will receive confirmation.</p>
              <p className="text-sm text-green-600 mt-3">Debt Reference: #{String(inviteInfo?.debtId).padStart(8, "0")}</p>
            </CardContent>
          </Card>
        ) : failed ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3 mb-3">
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
                <p className="font-semibold text-red-800 text-lg">Verification failed</p>
              </div>
              <p className="text-red-700 text-sm">The deposit amounts you entered don't match what Stripe sent to your account. Please double-check your bank statement and try again.</p>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => setFailed(false)}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="mb-6 border-blue-100 bg-blue-50/40">
              <CardContent className="pt-5 text-sm text-blue-800">
                <p className="font-medium mb-1">How to find your deposit amounts:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>Log into your online banking or check your statement</li>
                  <li>Look for two small deposits from "Stripe" or "STRD*" (each will be less than $1)</li>
                  <li>Enter both amounts below exactly as shown (in cents, e.g. "0.32")</li>
                </ol>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Confirm Micro-Deposit Amounts</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Enter the two small test deposit amounts Stripe sent to your bank account.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="amount1">First deposit ($)</Label>
                      <Input
                        id="amount1"
                        value={amount1}
                        onChange={(e) => setAmount1(e.target.value)}
                        placeholder="e.g. 0.32"
                        type="number"
                        step="0.01"
                        min="0.01"
                        max="0.99"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="amount2">Second deposit ($)</Label>
                      <Input
                        id="amount2"
                        value={amount2}
                        onChange={(e) => setAmount2(e.target.value)}
                        placeholder="e.g. 0.45"
                        type="number"
                        step="0.01"
                        min="0.01"
                        max="0.99"
                        required
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Verifying...</> : "Confirm & Verify Account"}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    Deposits typically appear within 1–2 business days after submitting your banking details.
                  </p>
                </form>
              </CardContent>
            </Card>
          </>
        )}

        <p className="text-xs text-center text-muted-foreground mt-8">
          Questions? Email <a href="mailto:support@debtbridge.org" className="underline">support@debtbridge.org</a>
        </p>
      </div>
      <QuoteCarousel />

    </div>
  );
}
